/**
 * Utilitários read-only para apresentação de extrato bancário na Customer Ledgers.
 * Não altera saldos, ledgers ou transações — apenas interpretação e ordenação visual.
 */

export const LEDGER_TYPE = {
  DEPOSIT: 0,
  WITHDRAWAL: 2,
  PERFORMANCE: 3,
  YIELD: 4,
};

const FINAL_WITHDRAWAL_STATUSES = new Set([3, 4, 5]);

/**
 * Converte Firestore Timestamp / Date / seconds object para milliseconds.
 */
export const parseTimestampToMs = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value?.seconds === 'number') {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1_000_000);
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Data contábil efetiva para ordenação do extrato (somente exibição).
 * Nunca regravar `date` no Firestore — YieldMonthlyProcessor depende dela.
 */
export const getEffectiveLedgerDateMs = (ledger) => {
  if (!ledger) return 0;

  const type = ledger.transection_type;
  const requestDateMs = parseTimestampToMs(ledger.request_date);
  const conclusionDateMs = parseTimestampToMs(ledger.conclusion_date);
  const ledgerDateMs = parseTimestampToMs(ledger.date);

  if (type === LEDGER_TYPE.WITHDRAWAL) {
    return requestDateMs ?? conclusionDateMs ?? ledgerDateMs ?? 0;
  }

  if (type === LEDGER_TYPE.DEPOSIT) {
    return conclusionDateMs ?? requestDateMs ?? ledgerDateMs ?? 0;
  }

  if (type === LEDGER_TYPE.PERFORMANCE || type === LEDGER_TYPE.YIELD) {
    return ledgerDateMs ?? conclusionDateMs ?? requestDateMs ?? 0;
  }

  return conclusionDateMs ?? requestDateMs ?? ledgerDateMs ?? 0;
};

export const getTransactionTypeName = (transectionType) => {
  const typeMap = {
    [LEDGER_TYPE.DEPOSIT]: 'Deposit',
    [LEDGER_TYPE.WITHDRAWAL]: 'Withdrawal',
    [LEDGER_TYPE.PERFORMANCE]: 'Performance',
    [LEDGER_TYPE.YIELD]: 'Yield',
  };
  return typeMap[transectionType] || 'Unknown';
};

export const getTransactionTypeColor = (transectionType) => {
  const colorMap = {
    [LEDGER_TYPE.DEPOSIT]: '#4caf50',
    [LEDGER_TYPE.WITHDRAWAL]: '#f44336',
    [LEDGER_TYPE.PERFORMANCE]: '#2196f3',
    [LEDGER_TYPE.YIELD]: '#ff8f00',
  };
  return colorMap[transectionType] || 'grey';
};

/**
 * Ordena ledgers por data contábil efetiva (mais recente primeiro).
 */
export const buildStatementRows = (ledgers = []) => {
  if (!Array.isArray(ledgers) || ledgers.length === 0) return [];

  return [...ledgers]
    .sort((a, b) => getEffectiveLedgerDateMs(b) - getEffectiveLedgerDateMs(a))
    .map((ledger) => {
      const balance =
        ledger.current_balance !== undefined && ledger.current_balance !== null
          ? parseFloat(ledger.current_balance)
          : ledger.balance !== undefined && ledger.balance !== null
            ? parseFloat(ledger.balance)
            : null;

      return {
        ...ledger,
        effectiveDateMs: getEffectiveLedgerDateMs(ledger),
        displayBalance: balance !== null && !Number.isNaN(balance) ? balance : null,
      };
    });
};

const getTxnAmount = (txn) =>
  parseFloat(txn?.offered_amount || txn?.total_amount || txn?.withdraw_amount || 0) || 0;

const isOpenWithdrawalStatus = (status) => !FINAL_WITHDRAWAL_STATUSES.has(status);

/**
 * Classifica transações pendentes para conciliação (não entram no extrato oficial).
 */
export const classifyPendingTransactions = (transactions = []) => {
  const pendingWithdrawalsDeducted = [];
  const pendingWithdrawalsApproved = [];
  const pendingDeposits = [];

  transactions.forEach((txn) => {
    const amount = getTxnAmount(txn);
    const status = txn.request_status;

    if (txn.request_type === 1 && isOpenWithdrawalStatus(status)) {
      if (txn.otp_verify === true && txn.balance_deducted === true) {
        pendingWithdrawalsDeducted.push({ ...txn, pendingAmount: amount });
      } else if (status === 2) {
        pendingWithdrawalsApproved.push({ ...txn, pendingAmount: amount });
      }
    }

    if (txn.request_type === 0 && (status === 0 || status === 1)) {
      pendingDeposits.push({ ...txn, pendingAmount: amount });
    }
  });

  const pendingDeductedTotal = pendingWithdrawalsDeducted.reduce(
    (sum, txn) => sum + txn.pendingAmount,
    0
  );

  return {
    pendingWithdrawalsDeducted,
    pendingWithdrawalsApproved,
    pendingDeposits,
    pendingDeductedTotal,
    hasPending: pendingWithdrawalsDeducted.length > 0 ||
      pendingWithdrawalsApproved.length > 0 ||
      pendingDeposits.length > 0,
  };
};

/**
 * Resumo de saldos para a modal — read-only.
 */
export const buildBalanceSummary = ({
  clientAppBalance = 0,
  statementRows = [],
  pendingDeductedTotal = 0,
}) => {
  const appBalance = parseFloat(clientAppBalance) || 0;
  const statementBalance =
    statementRows.length > 0 && statementRows[0].displayBalance !== null
      ? parseFloat(statementRows[0].displayBalance)
      : appBalance;

  const expectedAppFromStatement = statementBalance - pendingDeductedTotal;
  const unexplainedDifference = appBalance - expectedAppFromStatement;
  const isExplainedByPending =
    Math.abs(unexplainedDifference) < 0.02 && pendingDeductedTotal > 0;
  const rawDifference = appBalance - statementBalance;
  const isMismatch = Math.abs(rawDifference) >= 0.02;

  return {
    clientAppBalance: appBalance,
    statementBalance,
    pendingDeductedTotal,
    expectedAppFromStatement,
    unexplainedDifference,
    isExplainedByPending,
    isMismatch,
    shouldBlockFixBalance: isExplainedByPending || (isMismatch && pendingDeductedTotal > 0),
  };
};

/**
 * Yield não usa percentual do fundo — exibir "--" na coluna Profit (%).
 */
export const formatProfitPercentDisplay = (ledger) => {
  if (ledger?.transection_type === LEDGER_TYPE.YIELD) return '--';

  if (ledger?.profit_percentage != null && Math.abs(ledger.profit_percentage) > 0.0001) {
    return `${ledger.profit_percentage.toFixed(4)}%`;
  }

  if (ledger?.profit_loss_percent != null && Math.abs(ledger.profit_loss_percent) > 0.0001) {
    return `${ledger.profit_loss_percent.toFixed(4)}%`;
  }

  return '--';
};

/**
 * Nota sugerida para PDF Report quando há saques pendentes no momento do envio.
 * Avaliação registrada no plano — uso futuro em SendPdfMonthlyPerformance.
 */
export const buildPdfBalanceDisclaimer = (pendingDeductedTotal, asOfDate = new Date()) => {
  if (!pendingDeductedTotal || pendingDeductedTotal <= 0) return null;

  const formattedDate = asOfDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return `Updated balance as of ${formattedDate}. Pending withdrawals may affect the displayed balance.`;
};
